import { generateRoundRobin } from './roundRobinGenerator';
import { ordinal } from './bracketUtils';

const COURT_SIZE = 4;

/**
 * Which 4 incoming slots feed a given court next round, per the confirmed
 * adjacent-court promotion/relegation chain: Court 1 (top tier) always
 * receives the top 2 of Court 1 + top 2 of Court 2; the bottom court always
 * receives the bottom 2 of the second-to-last court + its own bottom 2;
 * every court in between receives the bottom 2 of the court above it and the
 * top 2 of the court below it. This rule is round-invariant — it describes
 * WHERE a slot comes from, not a specific round — which is exactly why every
 * round after Round 1 can reuse the same placeholder labels (see
 * buildPlaceholderCourtParticipants below).
 */
const incomingSlotsForCourt = (courtNumber, numCourts) => {
  if (courtNumber === 1) {
    return [
      { court: 1, rank: 1 },
      { court: 1, rank: 2 },
      { court: 2, rank: 1 },
      { court: 2, rank: 2 },
    ];
  }
  if (courtNumber === numCourts) {
    return [
      { court: numCourts - 1, rank: 3 },
      { court: numCourts - 1, rank: 4 },
      { court: numCourts, rank: 3 },
      { court: numCourts, rank: 4 },
    ];
  }
  return [
    { court: courtNumber - 1, rank: 3 },
    { court: courtNumber - 1, rank: 4 },
    { court: courtNumber + 1, rank: 1 },
    { court: courtNumber + 1, rank: 2 },
  ];
};

/**
 * Placeholder "participants" for a court in any round after Round 1 — shaped
 * exactly like groupKnockoutGenerator.js's qualifier placeholders
 * ({id, name, isPlaceholder: true}), so they flow through generateRoundRobin
 * and MatchListView identically to real participants, just rendered in
 * italic with a descriptive name instead of a real one.
 */
const buildPlaceholderCourtParticipants = (courtNumber, numCourts) =>
  incomingSlotsForCourt(courtNumber, numCourts).map((slot, i) => ({
    id: `tbd-c${courtNumber}-${i}`,
    name: `Court ${slot.court} · ${ordinal(slot.rank)}`,
    isPlaceholder: true,
  }));

/**
 * Ladder tournament — participants split into courts of 4 (by seed order:
 * Court 1 = ranks 1-4, Court 2 = ranks 5-8, ...), each court playing a full
 * round robin. Applies equally to singles and doubles — every generator
 * (including generateRoundRobin, reused per court here) treats a participant
 * as an opaque slot, so a doubles pair fills a court seat exactly the way a
 * singles entrant does.
 *
 * Only Round 1 is played with real participants — everything after depends
 * on actual results (who finishes top 2 / bottom 2 in each court), which
 * this generator has no way to know. Rather than stop at Round 1, every
 * subsequent round (up to `totalLadderRounds`) is still generated in full,
 * using PLACEHOLDER participants (see buildPlaceholderCourtParticipants) in
 * place of the real ones — the exact same "generate the shell, fill it in
 * later" approach group_knockout already uses for its knockout stage before
 * pool play concludes. A future per-format "seed" function is expected to
 * replace these placeholders with real participants round by round as
 * results come in; this generator only produces the shell.
 */
export const generateLadder = (participants, options = {}) => {
  if (!Array.isArray(participants) || participants.length < 8) {
    throw new Error('Ladder needs at least 8 participants');
  }
  if (participants.length % COURT_SIZE !== 0) {
    throw new Error('Ladder needs a participant count that\'s a multiple of 4 — one full court per group');
  }

  const numCourts = participants.length / COURT_SIZE;
  const totalLadderRounds = Math.max(1, options.ladderRounds || numCourts);

  const ladderRounds = [];
  for (let r = 1; r <= totalLadderRounds; r++) {
    const courts = [];
    for (let c = 1; c <= numCourts; c++) {
      const courtParticipants =
        r === 1
          ? participants.slice((c - 1) * COURT_SIZE, c * COURT_SIZE).map((p, i) => ({ ...p, rank: (c - 1) * COURT_SIZE + i + 1 }))
          : buildPlaceholderCourtParticipants(c, numCourts);
      courts.push({ court: c, participants: courtParticipants, rounds: generateRoundRobin(courtParticipants) });
    }
    ladderRounds.push({ ladderRound: r, courts });
  }

  return {
    formatId: 'ladder',
    participantsCount: participants.length,
    numCourts,
    totalLadderRounds,
    ladderRounds,
  };
};
